/**
 * Évaluateur multi-règles Pass (pur) — docs/EVOLUTION-PASS-MULTI-REGLES-CATEGORIES.md
 * D-MR1 : visit_count (+1/ticket) ; ticket_multi (toutes cats) ; cumul multi disjoints.
 */
import type {
  LoyaltyPassRule,
  LoyaltyPassRuleReward,
} from '../../types/loyaltyPass';
import {
  categoryRefsOverlap,
  loyaltyPassCategoryKey,
  normalizeLoyaltyPassRule,
  validateLoyaltyPassRule,
} from './rulesConfig';

export type LoyaltyPassSaleLineInput = {
  lineId: string;
  kind: 'service' | 'product';
  categoryId?: string;
  amountXaf: number;
  qty: number;
};

export type EvaluateSaleRulesInput = {
  rules: LoyaltyPassRule[];
  counters: Record<string, number>;
  lines: LoyaltyPassSaleLineInput[];
  transactionGroupId: string;
};

export type LoyaltyPassRuleAward = {
  ruleId: string;
  reward: LoyaltyPassRuleReward;
  eligibleLineIds: string[];
  discountAmountXaf: number;
};

export type EvaluateSaleRulesResult = {
  awards: LoyaltyPassRuleAward[];
  nextCounters: Record<string, number>;
  suppressedRuleIds: string[];
  lineDiscounts: Array<{
    lineId: string;
    ruleId: string;
    discountAmountXaf: number;
    percentOrFixed: LoyaltyPassRuleReward;
  }>;
};

function lineMatchesRule(
  line: LoyaltyPassSaleLineInput,
  rule: LoyaltyPassRule
): boolean {
  if (!line.categoryId) return false;
  if (rule.type === 'visit_count') return true;
  if (rule.categories.length === 0) return false;
  const key = loyaltyPassCategoryKey({
    kind: line.kind,
    categoryId: line.categoryId,
  });
  return rule.categories.some((c) => loyaltyPassCategoryKey(c) === key);
}

function eligibleLinesForRule(
  lines: LoyaltyPassSaleLineInput[],
  rule: LoyaltyPassRule
): LoyaltyPassSaleLineInput[] {
  if (rule.type === 'visit_count') return lines;
  return lines.filter((l) => lineMatchesRule(l, rule));
}

/** Ticket multi : chaque catégorie ciblée a ≥1 ligne. */
function isMultiTicketQualifying(
  lines: LoyaltyPassSaleLineInput[],
  rule: LoyaltyPassRule
): boolean {
  if (rule.type !== 'ticket_multi_category') return false;
  if (rule.categories.length === 0) return false;
  const present = new Set<string>();
  for (const line of lines) {
    if (!line.categoryId) continue;
    present.add(
      loyaltyPassCategoryKey({ kind: line.kind, categoryId: line.categoryId })
    );
  }
  return rule.categories.every((c) => present.has(loyaltyPassCategoryKey(c)));
}

function isVisitCountQualifying(lines: LoyaltyPassSaleLineInput[]): boolean {
  return lines.length > 0;
}

function discountOnAmount(reward: LoyaltyPassRuleReward, amountXaf: number): number {
  const base = Math.max(0, Math.floor(amountXaf));
  if (base <= 0) return 0;
  if (reward.type === 'fixed') {
    return Math.min(base, Math.max(0, Math.floor(reward.value)));
  }
  const pct = Math.min(100, Math.max(0, reward.value));
  return Math.floor((base * pct) / 100);
}

function estimateAwardAmount(
  reward: LoyaltyPassRuleReward,
  lines: LoyaltyPassSaleLineInput[]
): number {
  if (reward.discountBase === 'whole_ticket') {
    const total = lines.reduce((s, l) => s + Math.max(0, l.amountXaf), 0);
    return discountOnAmount(reward, total);
  }
  return lines.reduce((s, l) => s + discountOnAmount(reward, l.amountXaf), 0);
}

type Candidate = {
  rule: LoyaltyPassRule;
  eligibleLines: LoyaltyPassSaleLineInput[];
  provisionalCounter: number;
  estimatedDiscountXaf: number;
};

function compareCandidates(a: Candidate, b: Candidate): number {
  if (b.estimatedDiscountXaf !== a.estimatedDiscountXaf) {
    return b.estimatedDiscountXaf - a.estimatedDiscountXaf;
  }
  if (a.rule.priority !== b.rule.priority) {
    return a.rule.priority - b.rule.priority;
  }
  return a.rule.id.localeCompare(b.rule.id);
}

/**
 * Parmi les multi candidats : garde un gagnant par composante connexe
 * (intersection de cats) → cumul si cibles disjointes (§3bis.8).
 */
function pickWinningMultis(candidates: Candidate[]): {
  winners: Candidate[];
  suppressedIds: string[];
} {
  if (candidates.length === 0) {
    return { winners: [], suppressedIds: [] };
  }

  const remaining = [...candidates];
  const winners: Candidate[] = [];
  const suppressedIds: string[] = [];

  while (remaining.length > 0) {
    // Cluster = premier + tous ceux qui intersectent (fermeture transitive)
    const seed = remaining.shift()!;
    const cluster: Candidate[] = [seed];
    let grew = true;
    while (grew) {
      grew = false;
      for (let i = remaining.length - 1; i >= 0; i -= 1) {
        const cand = remaining[i]!;
        const overlapsCluster = cluster.some((c) =>
          categoryRefsOverlap(c.rule.categories, cand.rule.categories)
        );
        if (overlapsCluster) {
          cluster.push(cand);
          remaining.splice(i, 1);
          grew = true;
        }
      }
    }
    cluster.sort(compareCandidates);
    const win = cluster[0]!;
    winners.push(win);
    for (const loser of cluster.slice(1)) {
      suppressedIds.push(loser.rule.id);
    }
  }

  return { winners, suppressedIds };
}

/**
 * Évalue les règles sur un ticket (preview / commit).
 */
export function evaluateLoyaltyPassSaleRules(
  input: EvaluateSaleRulesInput
): EvaluateSaleRulesResult {
  const lines = input.lines.filter(
    (l) => typeof l.lineId === 'string' && l.lineId.length > 0
  );
  const activeRules = input.rules
    .map((r, i) => normalizeLoyaltyPassRule(r, i))
    .filter((r) => r.enabled && validateLoyaltyPassRule(r) === null);

  const counters: Record<string, number> = { ...input.counters };
  for (const r of activeRules) {
    if (typeof counters[r.id] !== 'number' || !Number.isFinite(counters[r.id])) {
      counters[r.id] = 0;
    }
  }

  const multiCandidates: Candidate[] = [];
  const visitCandidates: Candidate[] = [];

  for (const rule of activeRules) {
    const eligible = eligibleLinesForRule(lines, rule);
    const prev = counters[rule.id] ?? 0;

    if (rule.type === 'ticket_multi_category') {
      const qualifies = isMultiTicketQualifying(lines, rule);
      const delta = qualifies ? 1 : 0;
      const next = prev + delta;
      const awardLines =
        rule.reward.discountBase === 'whole_ticket' ? lines : eligible;
      if (delta > 0 && next >= rule.visitsRequired) {
        multiCandidates.push({
          rule,
          eligibleLines: awardLines,
          provisionalCounter: next,
          estimatedDiscountXaf: estimateAwardAmount(rule.reward, awardLines),
        });
      }
    } else {
      // visit_count (et legacy category_count normalisé)
      const qualifies = isVisitCountQualifying(lines);
      const delta = qualifies ? 1 : 0;
      const next = prev + delta;
      const awardLines =
        rule.reward.discountBase === 'whole_ticket' ? lines : eligible;
      if (delta > 0 && next >= rule.visitsRequired) {
        visitCandidates.push({
          rule,
          eligibleLines: awardLines,
          provisionalCounter: next,
          estimatedDiscountXaf: estimateAwardAmount(rule.reward, awardLines),
        });
      }
    }
  }

  const suppressedRuleIds: string[] = [];
  const { winners: winningMultis, suppressedIds: multiSuppressed } =
    pickWinningMultis(multiCandidates);
  suppressedRuleIds.push(...multiSuppressed);

  const multiAwards = winningMultis.length > 0;
  const survivingVisits: Candidate[] = [];
  if (multiAwards) {
    for (const c of visitCandidates) {
      suppressedRuleIds.push(c.rule.id);
    }
  } else if (visitCandidates.length > 0) {
    visitCandidates.sort(compareCandidates);
    const win = visitCandidates[0];
    if (win) survivingVisits.push(win);
    for (const c of visitCandidates.slice(1)) {
      suppressedRuleIds.push(c.rule.id);
    }
  }

  const structuralAwards: Candidate[] = [...winningMultis, ...survivingVisits];

  type LinePick = { candidate: Candidate; discountAmountXaf: number };
  const bestByLine: Record<string, LinePick> = {};

  for (const cand of structuralAwards) {
    const useLines =
      cand.rule.reward.discountBase === 'whole_ticket'
        ? lines
        : cand.eligibleLines;
    for (const line of useLines) {
      if (
        cand.rule.reward.discountBase !== 'whole_ticket' &&
        !lineMatchesRule(line, cand.rule)
      ) {
        continue;
      }
      const d = discountOnAmount(cand.rule.reward, line.amountXaf);
      const prev = bestByLine[line.lineId];
      if (
        !prev ||
        d > prev.discountAmountXaf ||
        (d === prev.discountAmountXaf &&
          cand.rule.priority < prev.candidate.rule.priority)
      ) {
        bestByLine[line.lineId] = { candidate: cand, discountAmountXaf: d };
      }
    }
  }

  const winningRuleIds = new Set(
    Object.values(bestByLine).map((v) => v.candidate.rule.id)
  );
  for (const w of winningMultis) {
    winningRuleIds.add(w.rule.id);
  }

  for (const cand of structuralAwards) {
    if (!winningRuleIds.has(cand.rule.id) && !suppressedRuleIds.includes(cand.rule.id)) {
      suppressedRuleIds.push(cand.rule.id);
    }
  }

  const awardsByRule: Record<string, LoyaltyPassRuleAward> = {};
  for (const lineId of Object.keys(bestByLine)) {
    const pick = bestByLine[lineId];
    if (!pick) continue;
    const { candidate, discountAmountXaf } = pick;
    const existing = awardsByRule[candidate.rule.id];
    if (existing) {
      existing.eligibleLineIds.push(lineId);
      existing.discountAmountXaf += discountAmountXaf;
    } else {
      awardsByRule[candidate.rule.id] = {
        ruleId: candidate.rule.id,
        reward: candidate.rule.reward,
        eligibleLineIds: [lineId],
        discountAmountXaf,
      };
    }
  }

  const awards = Object.values(awardsByRule);

  const nextCounters = { ...counters };
  const structuralAwardIds = new Set(structuralAwards.map((c) => c.rule.id));
  const winningMultiIds = new Set(winningMultis.map((c) => c.rule.id));

  for (const rule of activeRules) {
    const prev = nextCounters[rule.id] ?? 0;
    if (rule.type === 'ticket_multi_category') {
      const qualifies = isMultiTicketQualifying(lines, rule);
      if (!qualifies) continue;
      if (suppressedRuleIds.includes(rule.id) && !winningMultiIds.has(rule.id)) {
        continue;
      }
      nextCounters[rule.id] = prev + 1;
    } else {
      // visit_count : bloqué si un multi award
      if (multiAwards) {
        if (!suppressedRuleIds.includes(rule.id)) {
          suppressedRuleIds.push(rule.id);
        }
        continue;
      }
      if (isVisitCountQualifying(lines)) {
        nextCounters[rule.id] = prev + 1;
      }
    }
  }

  for (const cand of structuralAwards) {
    if (!structuralAwardIds.has(cand.rule.id)) continue;
    const cur = nextCounters[cand.rule.id] ?? 0;
    nextCounters[cand.rule.id] = Math.max(0, cur - cand.rule.visitsRequired);
  }

  const lineDiscounts = Object.keys(bestByLine).flatMap((lineId) => {
    const v = bestByLine[lineId];
    if (!v) return [];
    return [
      {
        lineId,
        ruleId: v.candidate.rule.id,
        discountAmountXaf: v.discountAmountXaf,
        percentOrFixed: v.candidate.rule.reward,
      },
    ];
  });

  return {
    awards,
    nextCounters,
    suppressedRuleIds: Array.from(new Set(suppressedRuleIds)),
    lineDiscounts,
  };
}
